import React, { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { SectionHeading } from "../components/SectionHeading";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { BRAND, ROADMAP, REVENUE_STREAMS, VISION, waLink } from "../lib/data";
import { submitInvestor } from "../lib/api";

const Icon = ({ name, className }) => { const C = Icons[name] || Icons.Circle; return <C className={className} />; };

export default function Investors() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", amount: "", interest: "", message: "", request_meeting: false });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error("Please fill name and phone"); return; }
    setLoading(true);
    try { await submitInvestor(form); toast.success("Thank you! Our investor relations team will reach out."); setForm({ name: "", phone: "", email: "", amount: "", interest: "", message: "", request_meeting: false }); }
    catch { toast.error("Could not submit. Try WhatsApp or call."); } finally { setLoading(false); }
  };

  return (
    <div data-testid="investors-page" className="pt-28 pb-20 bg-cream-100 text-soil-700 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-block text-xs uppercase tracking-[0.2em] font-bold text-terracotta-600 mb-3">Investor Relations</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-forest-800">Invest in India's <span className="saffron-text">Next Growth Story</span></h1>
          <p className="text-soil-500 mt-4">{VISION.financialVision} Join us on the journey from village to global markets.</p>
        </div>

        {/* Roadmap */}
        <SectionHeading overline="Vision 2032" title="Turnover Roadmap" subtitle="Year-on-year growth targets across the Genesis ecosystem." />
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-16">
          {ROADMAP.map((r, i) => (
            <motion.div key={r.year} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }} className="card-cream rounded-2xl p-5 text-center">
              <div className="font-mono-fin text-sm text-terracotta-600/70 font-bold">{r.year}</div>
              <div className="font-mono-fin text-2xl font-extrabold saffron-text mt-1">{r.target}</div>
              <div className="text-soil-500 text-xs mt-2">{r.note}</div>
            </motion.div>
          ))}
        </div>

        {/* Revenue streams */}
        <SectionHeading overline="Business Model" title="Diversified Revenue Streams" subtitle="Multiple high-growth engines de-risk the group and compound returns." />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {REVENUE_STREAMS.map((r, i) => (
            <motion.div key={r.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.07 }} className="card-cream rounded-2xl p-6">
              <div className="w-11 h-11 rounded-xl bg-gold/12 border border-saffron-500/30 flex items-center justify-center mb-3"><Icon name={r.icon} className="w-5 h-5 text-terracotta-600" /></div>
              <h3 className="font-heading font-semibold text-forest-800">{r.title}</h3>
              <p className="text-soil-500 text-sm mt-1">{r.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Enquiry */}
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          <div className="card-cream rounded-3xl p-8 ">
            <h2 className="font-heading text-2xl font-bold text-forest-800">Why Invest in Genesis?</h2>
            <ul className="mt-5 space-y-3">
              {["30-year legacy across 4 states", "10,000+ clients & a trusted brand (Loan Doctor)", "First-mover in India's cow-dung eco-industry", "Diversified, de-risked multi-sector model", "Clear ₹1,25,000 Cr vision by 2032"].map((p) => (
                <li key={p} className="flex items-start gap-3 text-soil-700"><Icons.CheckCircle2 className="w-5 h-5 text-approval shrink-0 mt-0.5" />{p}</li>
              ))}
            </ul>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button onClick={() => toast.success("Downloading Genesis pitch deck...")} data-testid="pitch-deck-button" variant="outline" className="border-forest-500/40 text-forest-700 hover:bg-forest-100 rounded-full h-11 px-5" asChild>
                <a href="/genesis-pitch-deck.pdf" download="Genesis-Group-Pitch-Deck.pdf"><Icons.Download className="w-4 h-4 mr-2" /> Download Pitch Deck</a>
              </Button>
              <a href={waLink("I'm an investor interested in Genesis Group")} target="_blank" rel="noreferrer nofollow"><Button className="bg-approval hover:bg-approval-dark text-forest-800 font-bold rounded-full h-11 px-5"><Icons.MessageCircle className="w-4 h-4 mr-2" /> WhatsApp</Button></a>
            </div>
          </div>

          <div className="card-cream rounded-3xl p-8">
            <h2 className="font-heading text-2xl font-bold text-forest-800 mb-6">Investor Enquiry</h2>
            <form onSubmit={submit} className="space-y-3.5" data-testid="investor-form">
              <div className="grid sm:grid-cols-2 gap-3.5">
                <Input data-testid="inv-name" placeholder="Full Name *" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-white border-cream-300 h-11 text-forest-800" />
                <Input data-testid="inv-phone" placeholder="Phone *" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-white border-cream-300 h-11 text-forest-800" />
              </div>
              <Input data-testid="inv-email" placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-white border-cream-300 h-11 text-forest-800" />
              <div className="grid sm:grid-cols-2 gap-3.5">
                <Input data-testid="inv-amount" placeholder="Investment Range (₹)" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="bg-white border-cream-300 h-11 text-forest-800" />
                <Input data-testid="inv-interest" placeholder="Sector of Interest" value={form.interest} onChange={(e) => set("interest", e.target.value)} className="bg-white border-cream-300 h-11 text-forest-800" />
              </div>
              <Textarea data-testid="inv-message" placeholder="Your message..." value={form.message} onChange={(e) => set("message", e.target.value)} className="bg-white border-cream-300 text-forest-800" />
              <div className="flex items-center justify-between rounded-xl bg-white border border-cream-300 px-4 py-3">
                <Label className="text-soil-600 text-sm">Request a meeting with the MD</Label>
                <Switch checked={form.request_meeting} onCheckedChange={(v) => set("request_meeting", v)} data-testid="inv-meeting" />
              </div>
              <Button type="submit" disabled={loading} data-testid="inv-submit" className="w-full forest-gradient text-cream-50 font-bold h-12 rounded-full">{loading ? <Icons.Loader2 className="w-5 h-5 animate-spin" /> : "Submit Enquiry"}</Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
